import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, CirclePlus } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { storeFinancialReconciliationTransactionData } from "@/Api/reconcialiationFinancialTransactions";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { FINANCIAL_TRANSACTION_CONST } from "@/constants/FinancialTransactionTypeConst";
import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  start_date: z.string({ message: "*requerido" }),
  end_date: z.string({ message: "*requerido" }),
  financial_transaction_type_id: z.union([z.string(), z.number()]),
});

const ALL_TYPES = "all";

type SchemaType = z.infer<typeof schema>;

export const AddReconciliationBtn = ({
  updateFront,
}: {
  updateFront: React.Dispatch<React.SetStateAction<any>>;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      start_date: "",
      financial_transaction_type_id: ALL_TYPES,
      end_date: "",
    },
  });

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");

    const payload = {
      name: data.name,
      start_date: data.start_date,
      financial_type_id:
        data.financial_transaction_type_id === ALL_TYPES
          ? null
          : data.financial_transaction_type_id,
      end_date: data.end_date,
    };

    try {
      const { data: apiResponse, ok } =
        await storeFinancialReconciliationTransactionData(payload);

      if (!ok) {
        setFormState("error");
        setTimeout(() => {
          setFormState("default");
        }, 1000);
        return;
      }

      updateFront((prev: any) => {
        return {
          ...prev,
          data: [
            {
              ...apiResponse?.response,
              is_active: 1,
            },
            ...prev?.data,
          ],
        };
      });

      toast({
        description:
          apiResponse?.message || "Conciliação adicionada com sucesso!",
        action: <Check color="#1edc2b" />,
      });

      form.reset();
      setFormState("default");
      setIsOpen(false);
    } catch (error) {
      console.error("Erro ao adicionar conciliação:", error);
      setFormState("error");
      setTimeout(() => {
        setFormState("default");
      }, 1000);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Adicionar Conciliação
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Adicionar Nova Conciliação
          </DialogDescription>
          <DialogTitle className="text-2xl">
            Informações da Conciliação
          </DialogTitle>
        </DialogHeader>

        <Form
          onSubmit={form.handleSubmit(formSubmit)}
          {...form}
          className="space-y-3"
        >
          <Form.Row>
            <Form.Field name="name">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Nome <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} placeholder="Nome da conciliação" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Field name="financial_transaction_type_id">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Tipo de Transação <Form.Message />
                  </Form.Label>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value?.toString()}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo de transação" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_TYPES}>Geral</SelectItem>
                      <SelectItem
                        value={FINANCIAL_TRANSACTION_CONST.ACCOUNT_RECEIVABLE.toString()}
                      >
                        Contas a Receber
                      </SelectItem>
                      <SelectItem
                        value={FINANCIAL_TRANSACTION_CONST.ACCOUNT_PAYABLE.toString()}
                      >
                        Contas a Pagar
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Row>
              <Form.Field name="start_date">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Data Inicial <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input type="date" {...field} />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <Form.Row>
              <Form.Field name="end_date">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Data Final <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input type="date" {...field} />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              className={cn("w-full", colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Adicionando <LoaderSpin />
                </>
              )}
              {formState === "default" && "Adicionar"}
              {formState === "error" && "Erro ao adicionar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
